import React, { useState, useRef, useEffect } from 'react';
import { MobileScrollRail } from './MobileScrollRail';
import { FaChevronLeft, FaChevronRight, FaArrowRight, FaNewspaper } from 'react-icons/fa';
import './News.css';
import { fetchNews } from '../firebase/firestoreService';
import { mergeFeaturedNews } from '../utils/featuredNews';

function formatNewsDate(value) {
  if (value == null || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function toNewsCard(n) {
  return {
    id: n.id,
    title: n.title,
    description: n.shortDescription,
    content: n.description,
    date: n.date,
    image: n.imageUrl,
    link: n.link || '',
    comingSoon: Boolean(n.comingSoon),
  };
}

const News = () => {
  const [activeNews, setActiveNews] = useState(null);
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  // Featured local news + Firestore CMS
  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        setError(null);
        let cmsItems = [];
        try {
          cmsItems = await fetchNews();
        } catch (cmsErr) {
          console.error('Error loading CMS news:', cmsErr);
        }
        setNewsList(mergeFeaturedNews(cmsItems).map(toNewsCard));
      } catch (err) {
        console.error('Error loading news:', err);
        setError('Failed to load news. Please try again later.');
        setNewsList(mergeFeaturedNews([]).map(toNewsCard));
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);

  const scroll = (direction) => {
    const amount = 300;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="news-component">
      <header className="news-header">
        <span className="news-section-tag">Press &amp; updates</span>
        <h2 className="news-title">Studio 13 News</h2>
        <div className="news-header-divider" aria-hidden />
        <p className="news-subtitle">Announcements, partnerships, and highlights from our immersive portfolio</p>
      </header>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading news...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>Retry</button>
        </div>
      ) : newsList.length > 0 ? (
        <div className="news-container">
          <button className="scroll-arrow scroll-left" onClick={() => scroll('left')}>
            <FaChevronLeft />
          </button>

          <div className="news-cards" ref={scrollContainerRef}>
            {newsList.map((item) => (
              <article
                key={item.id}
                className={`news-card${item.comingSoon ? ' news-card-coming-soon' : ''}`}
                role={item.comingSoon ? 'article' : 'button'}
                tabIndex={item.comingSoon ? undefined : 0}
                aria-label={
                  item.comingSoon
                    ? `${item.title} — Coming soon`
                    : `Open article: ${item.title}`
                }
                onClick={() => {
                  if (!item.comingSoon) setActiveNews(item);
                }}
                onKeyDown={(e) => {
                  if (item.comingSoon) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveNews(item);
                  }
                }}
              >
                <div className="news-image-container">
                  {item.image ? (
                    <img src={item.image} alt="" className="news-image" />
                  ) : item.comingSoon ? (
                    <div className="news-image-placeholder news-coming-soon-placeholder">
                      <span className="news-coming-soon-label">Coming soon</span>
                    </div>
                  ) : (
                    <div className="news-image-placeholder">
                      <FaNewspaper className="news-placeholder-icon" aria-hidden />
                    </div>
                  )}
                  {!item.comingSoon && (
                    <>
                      <div className="news-image-scrim" aria-hidden />
                      <div className="news-overlay">
                        <span className="read-more-pill">
                          Read more
                          <FaArrowRight className="read-more-arrow" aria-hidden />
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <div className="news-content">
                  <div className="news-meta">
                    {item.comingSoon ? (
                      <span className="news-date">Coming soon</span>
                    ) : (
                      <time className="news-date" dateTime={item.date || undefined}>
                        {formatNewsDate(item.date)}
                      </time>
                    )}
                    <span className="news-category">
                      {item.comingSoon ? 'Coming soon' : 'News'}
                    </span>
                  </div>
                  <h3 className="news-card-title">{item.title}</h3>
                  {item.description ? (
                    <p className="news-summary">{item.description}</p>
                  ) : null}
                  {!item.comingSoon && (
                    <span className="news-card-cta">
                      View story
                      <FaArrowRight className="news-card-cta-icon" aria-hidden />
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>

          <MobileScrollRail scrollerRef={scrollContainerRef} />

          <button className="scroll-arrow scroll-right" onClick={() => scroll('right')}>
            <FaChevronRight />
          </button>
        </div>
      ) : (
        <div className="no-projects">
          <p>No news available at the moment.</p>
        </div>
      )}

      {activeNews && (
        <div className="news-modal" onClick={() => setActiveNews(null)} role="presentation">
          <div
            className="news-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="news-modal-title"
          >
            <div className="modal-accent-bar" aria-hidden />
            <div className="modal-header">
              <h3 id="news-modal-title">{activeNews.title}</h3>
              <button
                type="button"
                className="close-modal"
                onClick={() => setActiveNews(null)}
                aria-label="Close article"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {activeNews.image && (
                <img
                  src={activeNews.image}
                  alt=""
                  className="modal-image"
                />
              )}
              <div className="modal-meta">
                <time className="modal-date" dateTime={activeNews.date || undefined}>
                  {activeNews.comingSoon ? 'Coming soon' : formatNewsDate(activeNews.date)}
                </time>
                <span className="modal-category">
                  {activeNews.comingSoon ? 'Coming soon' : 'Studio 13 News'}
                </span>
              </div>
              <div className="modal-content">
                {String(activeNews.content || '')
                  .split(/\n+/)
                  .filter(Boolean)
                  .map((para, index) => (
                    <p key={`news-para-${index}`}>{para}</p>
                  ))}
                {activeNews.link && (
                  <a
                    href={activeNews.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-link"
                  >
                    Visit source
                    <FaArrowRight className="modal-link-icon" aria-hidden />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
